import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useAuthProvider, AuthProvider } from '@maui-mf/common-react'
import { useIsGTI } from '@maui-mf/root'
import { AUTH_REDIRECT_PATH } from '@maui-mf/common-auth'
import { useDispatch, useSelector } from 'react-redux'
import { genHasSsoApplications } from './auth/ssoApplications'
import {
  hasAnyMaAdminRole,
  hasRole,
  hasDTMRoles,
  hasAnyAdminRole,
  hasMaAdminRole,
  hasMaAdminRoles,
  hasOrgAdminRole,
  hasOrgRoles,
} from './auth/roles'
import { genHasEntitlements, userAccessToResource } from './auth/entitlements'
import {
  userConfirmationRequest,
  userLogout,
  provisioningRequest,
} from './store/auth/actions'
import * as entitlements from './entitlements'
import env from './env'

const AuthContext = React.createContext({})

const AdaptedAuthProvider = ({ children }) => {
  const dispatch = useDispatch()

  // NOTE we're not taking userinfo into account here (yet) because the auth store doesn't
  const { claims, userinfo, token, isGTI } = useAuthProvider()
  const { ux: isGTIUX, uxGranted: isGTIUXGranted } = useIsGTI()

  // NOTE derive the user from the claims & userinfo
  const user = useMemo(() => {
    if (!claims) {
      return undefined
    }

    // It'd be really nice to fix this, why the remapping?
    // NOTE the legacy formatting happens in the common auth package
    const orgs = claims.organizations.map(org => ({
      orgUuid: org.uuid,
      orgName: org.name,
    }))

    const entitlements = claims?.isl || []

    // NOTE it'd probably be best to NOT do this here and create inconsistency
    // between the claims and the derived "user"
    const exp = claims.exp * 1000

    return {
      email: claims.email,
      entitlements,
      exp,
      firstName: claims.first_name,
      lastName: claims.last_name,
      orgName: orgs[0].orgName,
      orgUuid: claims.org,
      orgs,
      ssoApplications: claims.sso_applications,
      token,
      userType: claims.user_type,
      uuid: claims.sub,
    }
  }, [claims, token])

  useEffect(() => {
    if (!user) {
      dispatch(userLogout())
    }
  }, [user, dispatch])

  useEffect(() => {
    if (user) {
      dispatch(provisioningRequest({ user }))
    }
  }, [user, dispatch])

  useEffect(() => {
    if (user && window._paq) {
      window._paq.push(['setUserId', user.uuid])
    }
  }, [user])

  const profile = useSelector(state => state.auth.profile)

  const confirm = useCallback((params) => {
    dispatch(userConfirmationRequest(params))
  }, [dispatch])

  const value = useMemo(() => {
    const hasEntitlements = genHasEntitlements(user)
    const hasSsoApplications = genHasSsoApplications(user)

    return {
      isAdmin: !!user && !!user.email.match(/fireeye.com|mandiant.com/),
      confirm,
      hasEntitlements,
      hasRole: role => hasRole(role, claims?.org, profile),
      hasOrgRoles: roles => hasOrgRoles(roles, claims?.org, profile),
      isFreemium: !hasEntitlements(['INTEL_RBI_FUS', 'INTEL_RBI_OPS', 'INTEL_RBI_VULN']),
      isMultiOrg: Array.isArray(claims?.organizations) && claims.organizations.length > 1,
      hasAnyAdminRole: () => hasAnyAdminRole(claims?.org, profile),
      hasOrgAdminRole: () => hasOrgAdminRole(claims?.org, profile),
      hasMaAdminRole: (role) => hasMaAdminRole(role, profile),
      hasAnyMaAdminRole: () => hasAnyMaAdminRole(profile),
      hasMaAdminRoles: (roles) => hasMaAdminRoles(roles, profile),
      hasFusionAccess: hasEntitlements(['INTEL_RBI_FUS']),
      hasIntelAccess: hasEntitlements(['INTEL_RBI_FUS', 'INTEL_RBI_OPS', 'INTEL_RBI_VULN']),
      hasDTMAccess: hasEntitlements(entitlements.dtm) && hasDTMRoles(claims?.org, profile),
      hasDTMLicenses: claims?.isl.includes('INTEL_DTM_STNDRD'),
      hasVulnAccess: hasEntitlements(['INTEL_RBI_VULN']),
      hasEIBAccess: hasEntitlements(['INTEL_EIB']),
      hasMDAccess: hasSsoApplications(['MD_PORTAL']),
      hasMSVAccess: hasEntitlements('MA_MSV'),
      hasPOV: hasEntitlements(['INTEL_FUSION_POV', 'INTEL_OPER_POV', 'INTEL_VULN_POV']),
      hasPaidSubscription: hasEntitlements([
        'ASM_PAID',
        'ASM_3RD_PARTY',
        'INTEL_RBI_FUS',
        'INTEL_RBI_OPS',
        'INTEL_RBI_VULN',
        'INTEL_FREEMIUM',
        'INTEL_DTM_STNDRD',
        'INTEL_DTM_ADV',
        'INTEL_EOD',
      ]),
      hasThreatProfilesAccess: hasEntitlements(['INTEL_RBI_FUS', 'INTEL_RBI_OPS']),
      hasAccessToObject: object => userAccessToResource(object, user),
      isGTI,
      isGTIUX,
      isGTIUXGranted,
      isLoggedIn: !!user,
      licenses: claims?.isl,
      organizations: claims?.organizations,
      profile,
      selectedOrgUuid: claims?.org,
      // NOTE returnTo is generally handled by the auth app, currently just returning "/" here
      //      always which the app router will redirect to if the user tries to hit an
      //      "unauthed only" route like the login page while logged in.
      returnTo: '/',
      user,
      userinfo,
    }
  }, [claims, confirm, isGTI, isGTIUX, isGTIUXGranted, user, profile, userinfo])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// NOTE we need this because of the org switcher, when the org switcher becomes global it
// won't be necessary for the app to be aware of SSO
const authProviderProps = {
  clientId: env.MAUI_IDP_CLIENT_ID,
  host: env.MAUI_IDP_HOST,
  redirectUri: new URL(AUTH_REDIRECT_PATH, window.location).toString()
}

export function AuthContextProvider({ children }) {
  return (
    <AuthProvider {...authProviderProps}>
      <AdaptedAuthProvider>
        {children}
      </AdaptedAuthProvider>
    </AuthProvider>
  )
}

export function useAuthContext() {
  return useContext(AuthContext)
}

export default AuthContext
